import { TradingPlatForms } from '@src/types/onboarding.ts';
import * as yup from 'yup';

export enum AccountActivityDisplay {
  ShowEvals = 'show_evals',
  ShowPas = 'show_pas',
  ShowAddons = 'show_addons',
}
export enum AccountActivityActiveStatus {
  LiveOnly = 'live_only',
  AllAccounts = 'all_accounts',
}
export enum Notes {
  ALl = 'all',
  Flag = 'flag',
  Payouts = 'payout',
}
export enum UserType {
  Users = 'users',
  DuplicateUsers = 'duplicate_users',
}

export enum FindDuplicatesByType {
  PhoneNumber = 'phone_number',
  Address = 'address',
}

export type UserReq = {
  duplicate_by?: string;
  expire_date_after?: string;
  search_words?: string;
  page?: number;
  start_date?: string;
  end_date?: string;
  country?: string;
};

export type UserInfo = Partial<{
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  fullname: string;
  street: string | null;
  zip_code: string | null;
  country: string | null;
}>;
export type LeelooAccounts = Partial<{
  id: number;
  rithmic_account_id: string;
  rithmic_account_name: string;
  tradovate_account_id: string;
  tradovate_account_name: string;
  valid_from: string;
  valid_to: string;
  created_at?: string;
  account_type: 'tradovate' | 'rithmic' | string;
  trading_days: number;
  carried_trades: number;
  extra_trading_days: number;
  account_balance: number;
  cash_balance: number;
  account_balance_date: string | null;
  altv: number;
  status: 'pending' | 'active' | 'inactive' | string;
  qualifying_status: string;
  qualify_expire: string;
  qualifying_status_detail: string | null;
  qualifying_account_name: string | null;
  product: Product;
  is_pro: boolean | null;
}>;

export type LeelooUser = {
  id: number;
  user_type: TradingPlatForms;
  username?: string;
  first_name?: string;
  street?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  city?: string;
  last_name?: string;
  is_flagged?: boolean;
  is_watched?: boolean;
  copy_trading?: boolean;
  copy_trading_users?: boolean;
  active_pa?: number;
  payment?: number;
  total_payment_amount?: string;
  expire_date?: string;
  user: UserInfo;
  leeloo_accounts: LeelooAccounts[];
  email: string;
  phone_number?: string;
};

export type UserRes = {
  current_page: number;
  has_next: boolean;
  leeloo_users: LeelooUser[];
  total: number;
  total_pages: number;
};

// Minimal search item used for header suggestions
export type UserSearchItem = {
  id: number;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

export type DuplicateAccount = {
  id: number;
  phone_number: string | null;
  address: string | null;
  username: string;
  user: UserInfo;
};

export type DuplicateUserRes = {
  total: number;
  phone_number: string;
  address: string | null;
  actives: DuplicateAccount[];
  inactives: DuplicateAccount[];
};

export type TAccountActivity = {
  userId?: string;
  userName?: string;
  accountId?: string;
  package?: string;
  trading?: number;
  balance?: number;
  balancedAt?: string;
  status?: string;
};

export type TNotesTable = {
  userId?: string;
  date?: string;
  userName?: string;
  note?: string;
};

export type TUserTable = {
  id?: string;
  userName?: string;
  userId?: string;
  accountNo?: string;
  accountDes?: string;
  package?: string;
  phoneNumber?: string;
  isActive?: boolean;
  totalProfit?: number;
  maxAllowedDailyProfit?: number;
  highestDailyProfit?: number;
  highestDailyProfitPercent?: number;
  tradingDays?: number;
  lastPayout?: string;
  accountPayouts?: string;
  customerPayouts?: number;
  totalCustomerPayouts?: number;
};

interface Product {
  title: string;
}

export type Payment = {
  id: number;
  invoice_id: number;
  user_id: number;
  payment_system: string;
  external_transaction_id: string;
  external_receipt_id: string;
  display_invoice_id: string;
  amount: string;
  discount_amount: string;
  tax_amount: string;
  shipping_amount: string;
  currency: string;
  status: string;
  paid_at: string;
  refunded_at: string | null;
  refunded_amount: string;
  created_at: string;
  updated_at: string;
  invoice_external_id: string;
  invoice_status: string;
  invoice_coupon_code: string | null;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_country: string;
  user_created_at: string;
  product_id: number;
  product_title: string;
};

export type UserPayment = {
  user_id: number;
  payments: Payment[];
  total_count: number;
  total_amount: string;
  total_tax: string;
};

export interface UserProfile {
  cbot: boolean;
  city: string | null;
  cme: boolean;
  comex: boolean;
  company_name: string | null;
  country: string | null;
  zip: string | null;
  eurex: boolean;
  expire_date: string | null;
  first_name: string | null;
  id: number;
  is_company: boolean | null;
  is_watched: boolean | null;
  is_pro: boolean | null;
  last_name: string | null;
  referral_source: string | null;
  group_ids?: any[];
  filtered_accounts: LeelooAccounts[];
  max_session_data: number;
  total_free_accounts: number;
  total_refund: number;
  nymex: boolean | null;
  phone_number: string | null;
  q: boolean | null;
  qualifying_account_name: string | null;
  qualifying_status_detail: string | null;
  state: string | null;
  street: string | null;
  user_type: TradingPlatForms | null;
  username: string;
  user: UserInfo;
  zip_code: string | null;
  email: string | null;
  rise_works_wallet: string | null;
  rise_works_confirmed: string | null;
  rise_works_invited: string | null;
  rise_works_status: string | null;
  rise_works_id: string | null;
  allow_paypal: boolean | null;
  allow_wire: boolean | null;
  allow_coupon: boolean | null;
  allow_riseworks: boolean | null;
  tradovate_id: string | null;
  tradovate_terms_signed: string | null;
  tradovate_subscription_id: string | null;
  tradovate_subscription_status: string | null;
  tradovate_market_data_id: string | null;
  tradovate_valid_to: string | null;
  tradovate_market_data_valid_to: string | null;
  tradovate_contact_id: string | null;
  trader_paid: number | null;
  leeloo_paid: number | null;
  is_approved?: number | null;
  unsubscribed?: number | null;
  is_locked?: number | null;
  internal_notes?: string | null;
  lang?: string | null;
  businessname?: string | null;
}

export type PaymentReq = {
  product_id: number | null;
  payment_system: string | null;
  payment_start_date: string | null;
  payment_end_date: string | null;
  user_added_start_date: string | null;
  user_added_end_date: string | null;
  search_word?: string | null;
  search_field?: string | null;
};

export type UpdateUserReq = {
  company_name?: string;
  is_company?: boolean;
  username?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  street?: string;
  lang?: string;
  city?: string;
  state?: string | null;
  businessname?: string | null;
  zip?: string | null;
  country?: string;
  zip_code?: string;
  email?: string;
  password?: string;
  referral_source?: string;
  internal_notes?: string;
  is_locked?: boolean;
  is_approved?: boolean;
  unsubscribed?: boolean;
  groups?: number[];
};

export type UserNote = {
  can_delete: boolean | null;
  created_at: string;
  user: UserInfo;
  content: string | null;
  id: number;
  is_access_log: boolean | null;
  leeloo_user_id: number;
  user_id: number;
};

export type UserNoteItem = {
  id: number;
  admin_id: number;
  user_id: number;
  content: string;
  attachments: string;
  created_at: string;
  admin: any;
  updated_at: string;
};

export type UserNotesResponse = {
  notes: UserNoteItem[];
  total: number;
};

export type UserDetailQuery = {
  show_evals?: boolean;
  show_pas?: boolean;
  show_addons?: boolean;
  live_only?: boolean;
};

export const editBalanceSchema = yup.object({
  current_balance: yup.number().required('Field required'),
  new_balance: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .required('New Balance is required'),
  extra_trading_days: yup
    .string()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('current_balance', {
      is: (val: string) => !val,
      then: (schema) => schema.required('Trading days is required'),
      otherwise: (schema) => schema.optional(),
    }),
  notes: yup.string().required('Notes is required'),
});

export type EditBalanceFormData = yup.InferType<typeof editBalanceSchema>;

export const creditSchema = yup.object({
  amount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .required('Amount is required'),
  comment: yup.string().required('Comment is required'),
});

export type CrRefundFormData = yup.InferType<typeof creditSchema>;

export const payoutRequestSchema = yup.object({
  rithmic_deduction_amount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .min(250, 'Choose an amount from $250 to $1,200')
    .max(1200, 'Choose an amount from $250 to $1,200'),
  payout_amount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .min(250, 'Choose an amount from $250 to $1,200')
    .max(1200, 'Choose an amount from $250 to $1,200')
    .required('Payout Amount is required'),
  override: yup.boolean().optional(),
  note: yup.string().optional(),
  payment_methods: yup.string().required('Field is required'),
});

export type PayoutRequestFormData = yup.InferType<typeof payoutRequestSchema>;

export type CloseUser = {
  leeloo_user_id: number;
  leeloo_account_id: number;
};
